import React, { useState, useCallback, useEffect } from 'react';
import ComboboxComFiltro from '../../components/autocomplete';

import logoPadrao from '../../assets/sem-logo.png';

import {
  Container,
  ContainerFields,
  ContainerMonetizacoes,
  ConfirmButton,
  ContainerButton,
  InputCategoria,
  InputCusto,
  InputPreco,
  CardMonetizacao,
} from './styles';

import api from '../../services/api';

import IConvenio from '../../routes/vms/IConvenio';
import ICategoria from '../../routes/vms/ICategoria';
import IMonetizacao from '../../routes/vms/IMonetizacao';

import { useToast } from '../../context/ToastContext';

import { FiPlus, FiCheck, FiTrash } from 'react-icons/fi';

import returnValidNumber from '../../utils/returnValidNumber';
import { returnAxiosError } from '../../utils/returnAxiosError';

interface EsperaProps {
  categoria_param: ICategoria | null;
}

const Monetizacao: React.FC<EsperaProps> = ({ categoria_param }) => {
  const { addToast } = useToast();

  const [convenios, setConvenios] = useState<IConvenio[]>([]);

  const [categoria, setCategoria] = useState('');
  const [custoGeral, setCustoGeral] = useState('');
  const [precoGeral, setPrecoGeral] = useState('');

  const [convenio, setConvenio] = useState<string | null>(null);
  const [custoConvenio, setCustoConvenio] = useState('');
  const [precoConvenio, setPrecoConvenio] = useState('');

  const [monetizacoes, setMonetizacoes] = useState<IMonetizacao[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  const verificaCustoPreco = useCallback(
    (custo: string, preco: string) => {
      if (!returnValidNumber(custo)) {
        addToast({
          title: 'Erro',
          description: 'Custo inválido',
          type: 'error',
        });

        return false;
      }

      if (!returnValidNumber(preco)) {
        addToast({
          title: 'Erro',
          description: 'Preço inválido',
          type: 'error',
        });

        return false;
      }

      return true;
    },
    [addToast],
  );

  const limpaCampos = useCallback(() => {
    setConvenio(null);
    setCustoConvenio('');
    setPrecoConvenio('');
  }, []);

  const atualizaDados = useCallback(async () => {
    try {
      const [conveniosResponse, monetizacoesResponse] = await Promise.all([
        api.get<IConvenio[]>('/convenio/lista'),
        api.get<IMonetizacao[]>(
          `/categoria/${categoria_param?.id}/lista-monetizacoes`,
        ),
      ]);

      setConvenios(conveniosResponse.data);
      setMonetizacoes(monetizacoesResponse.data);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  }, [categoria_param]);

  const aoAlterarConvenio = (convenio: string | null) => {
    setConvenio(convenio);
  };

  const aoAlterarCustoGeral = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCustoGeral(event.target.value);
    },
    [],
  );

  const aoAlterarPrecoGeral = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPrecoGeral(event.target.value);
    },
    [],
  );

  const aoAlterarCustoConvenio = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCustoConvenio(event.target.value);
    },
    [],
  );

  const aoAlterarPrecoConvenio = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPrecoConvenio(event.target.value);
    },
    [],
  );

  const handleExcluirMonetizacao = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/categoria/monetizacao/${id}`);

        atualizaDados();

        addToast({
          type: 'success',
          title: 'Monetização excluída!',
        });
      } catch (error) {
        addToast(returnAxiosError(error));

        return;
      }
    },
    [addToast, atualizaDados],
  );

  const handleIncluirMonetizacao = useCallback(async () => {
    try {
      if (!verificaCustoPreco(custoConvenio, precoConvenio)) {
        return;
      }

      await api.post('/categoria/monetizacao', {
        categoria_id: categoria_param?.id,
        convenio_id: convenio,
        custo: returnValidNumber(custoConvenio),
        preco: returnValidNumber(precoConvenio),
      });

      limpaCampos();
      atualizaDados();

      addToast({
        type: 'success',
        title: 'Parabéns!',
        description: 'Monetização cadastrada com sucesso!',
      });
    } catch (error) {
      addToast(returnAxiosError(error));

      return;
    }
  }, [
    addToast,
    custoConvenio,
    precoConvenio,
    verificaCustoPreco,
    categoria_param,
    convenio,
    atualizaDados,
    limpaCampos,
  ]);

  const handleAlterarValoresGerais = useCallback(async () => {
    try {
      if (!verificaCustoPreco(custoGeral, precoGeral)) {
        return;
      }

      await api.patch(`/categoria/${categoria_param?.id}/incluir-valores`, {
        custo_geral: returnValidNumber(custoGeral),
        preco_geral: returnValidNumber(precoGeral),
      });

      setCustoGeral(String(returnValidNumber(custoGeral)));
      setPrecoGeral(String(returnValidNumber(precoGeral)));

      addToast({
        type: 'success',
        title: 'Parabéns!',
        description: 'Custo e preço alterados com sucesso!',
      });
    } catch (error) {
      addToast(returnAxiosError(error));

      return;
    }
  }, [categoria_param, custoGeral, precoGeral, addToast, verificaCustoPreco]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (categoria_param) {
      setCategoria(categoria_param.nome);
      setCustoGeral(String(categoria_param.custo_geral));
      setPrecoGeral(String(categoria_param.preco_geral));
    }

    atualizaDados();
  }, [categoria_param, atualizaDados]);

  return (
    <Container>
      <h2>Monetização</h2>

      <ContainerFields>
        <InputCategoria
          variant="outlined"
          margin="normal"
          label=" Categoria"
          value={categoria}
          disabled={true}
        />

        <InputCusto
          variant="outlined"
          margin="normal"
          label="Custo Geral"
          value={custoGeral}
          onChange={aoAlterarCustoGeral}
        />

        <ContainerButton>
          <InputPreco
            variant="outlined"
            margin="normal"
            label="Preço Geral"
            value={precoGeral}
            onChange={aoAlterarPrecoGeral}
          />

          <ConfirmButton onClick={handleAlterarValoresGerais}>
            <FiCheck />
          </ConfirmButton>
        </ContainerButton>
      </ContainerFields>

      <h3>Monetização por convênio</h3>

      <ContainerFields>
        <ComboboxComFiltro
          opcoes={convenios}
          value={convenio}
          label="Convênio"
          onChange={aoAlterarConvenio}
          size={4}
        />

        <InputCusto
          variant="outlined"
          margin="normal"
          label="Custo"
          value={custoConvenio}
          onChange={aoAlterarCustoConvenio}
        />

        <ContainerButton>
          <InputPreco
            variant="outlined"
            margin="normal"
            label="Preço"
            value={precoConvenio}
            onChange={aoAlterarPrecoConvenio}
          />

          <ConfirmButton onClick={() => handleIncluirMonetizacao()}>
            <FiPlus />
          </ConfirmButton>
        </ContainerButton>
      </ContainerFields>

      <ContainerMonetizacoes>
        {monetizacoes.map(monetizacao => (
          <CardMonetizacao key={monetizacao.id}>
            <div>
              <h4>{monetizacao.convenio.nome}</h4>

              {isMobile || (
                <img
                  src={
                    !monetizacao.convenio.logo_url
                      ? logoPadrao
                      : monetizacao.convenio.logo_url
                  }
                  alt="logo convênio"
                />
              )}
            </div>

            <div>
              <span>
                <p>Custo:</p>
                <div>{`R$ ${Number(monetizacao.custo).toFixed(2)}`}</div>
              </span>

              <span>
                <p>Preço:</p>
                <div>{`R$ ${Number(monetizacao.preco).toFixed(2)}`}</div>
              </span>

              <FiTrash
                onClick={() => handleExcluirMonetizacao(monetizacao.id)}
              />
            </div>
          </CardMonetizacao>
        ))}
      </ContainerMonetizacoes>
    </Container>
  );
};

export default Monetizacao;
